import { addYears, format, isValid, parseISO, parse } from "date-fns";
import Cookies from "js-cookie";

import { clearToken, getToken } from "../../api/auth";
import axios from "../../api/axios";

import { userDBService } from "./dbInstances";

export function isFormComplete(form, ignoredFields = []) {
  if (!form) {
    return false;
  }

  return Object.entries(form).every(([key, value]) => {
    if (ignoredFields.includes(key)) {
      return true;
    }

    return value !== null && value !== undefined && value !== "";
  });
}

export function getExtension(filename = "") {
  if (filename && filename.includes(".")) {
    return filename.split(".").pop();
  } else {
    return "";
  }
}

export const sortNumbers = (val1, val2) => val1 - val2;
export const sortStrings = (val1 = "", val2 = "") =>
  val1.toLowerCase().localeCompare(val2.toLowerCase());
export const sortDates = (val1, val2) => new Date(val1) - new Date(val2);

export const invalidateSession = async ({ redirectTo = "/", skipRedirect = false } = {}) => {
  const cookiesToRemove = ["pnCmPk", "userType", "category"];
  const token = getToken();

  if (token) {
    try {
      await axios.post("/auth/logout", null, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
    } catch {
      // Ignore network errors during logout; cleanup still runs.
    }
  }
  localStorage.removeItem("token_exp");
  clearToken();
  cookiesToRemove.forEach((cookie) => Cookies.remove(cookie, { path: "/" }));
  await userDBService.clear();
  if (!skipRedirect) {
    window.location.href = redirectTo;
  }
};

export function onScrollDown(sectionId) {
  setTimeout(() => {
    const section = document.getElementById(sectionId);
    section && section.scrollTo({ top: section.scrollHeight, behavior: "smooth" });
  }, 500);
}

export function calculateAge(dateOfBirth) {
  const today = new Date();
  const birthdayDate = new Date(dateOfBirth);

  let age = today.getFullYear() - birthdayDate.getFullYear();
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();
  const birthMonth = birthdayDate.getMonth();
  const birthDay = birthdayDate.getDate();

  // Adjust if you have not yet turned this year
  if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
    age--;
  }

  return age;
}

export const formatDateTime = (iso) => {
  if (!iso) {
    return "";
  }

  const date = new Date(iso);

  const datePart = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);

  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  return `${datePart} - ${timePart}`;
};

/**
 *
 * @param {*} dateString In format "YYYY-MM-DD"
 * @param {*} desireFormat Add your desire format. By default would be: "dd/MM/yyyy"
 * @returns A formatted date on an specific format
 */
export function formatDate(dateString, desiredFormat = "MM/dd/yyyy") {
  if (!dateString) {
    return "N/A";
  }

  const isLocalDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  if (isLocalDateOnly) {
    const [year, month, day] = dateString.split("-");
    const localDate = new Date(Number(year), Number(month) - 1, Number(day));
    return format(localDate, desiredFormat);
  }

  try {
    const parsed = parseISO(dateString);
    if (isNaN(parsed.getTime())) {
      return "N/A";
    }
    return format(parsed, desiredFormat);
  } catch {
    return "N/A";
  }
}

export function isValidIsoDate(dateStr) {
  if (typeof dateStr !== "string") {
    return false;
  }
  const date = new Date(dateStr);
  return !isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}T/.test(dateStr);
}

export function formatIsoOrTime(dateStr) {
  if (typeof dateStr !== "string" || !dateStr.trim()) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    try {
      return format(parseISO(dateStr), "MM/dd/yyyy");
    } catch {
      return dateStr;
    }
  }

  if (/^\d{2}:\d{2}(:\d{2})?$/.test(dateStr)) {
    try {
      return format(parseISO(`1970-01-01T${dateStr}`), "hh:mm a");
    } catch {
      return dateStr;
    }
  }

  const date = new Date(dateStr);
  if (!isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}T/.test(dateStr)) {
    return format(date, "MM/dd/yyyy hh:mm a");
  }

  return dateStr;
}

export function parseValue(val, propType) {
  switch (propType) {
    case "integer":
      return val === "" ? null : parseInt(val, 10);
    case "number":
      return val === "" ? null : parseFloat(val);
    case "boolean":
      return val === "true" || val === true;
    default:
      return val;
  }
}

export function compareArrayData(currentData, initialData) {
  const changes = currentData.filter((item, idx) => {
    const original = initialData[idx];
    return !original || JSON.stringify(item) !== JSON.stringify(original);
  });
  return changes;
}

export const toValidDate = (value, fallback = null) => {
  if (!value) {
    return fallback;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : fallback;
  }

  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const localParsed = parse(value, "yyyy-MM-dd", new Date());
      if (isValid(localParsed)) {
        return localParsed;
      }
    }

    const isoParsed = parseISO(value);
    if (isValid(isoParsed)) {
      const hasTimeComponent = value.includes("T");
      const hasNonZeroTime =
        isoParsed.getUTCHours() !== 0 ||
        isoParsed.getUTCMinutes() !== 0 ||
        isoParsed.getUTCSeconds() !== 0 ||
        isoParsed.getUTCMilliseconds() !== 0;

      if (hasTimeComponent && hasNonZeroTime) {
        return isoParsed;
      }

      return new Date(isoParsed.getUTCFullYear(), isoParsed.getUTCMonth(), isoParsed.getUTCDate());
    }
  }

  const candidate = new Date(value);
  return isValid(candidate) ? candidate : fallback;
};

export const getCenturyDateBounds = () => {
  const now = new Date();

  return {
    min: new Date(1900, 0, 1),
    max: addYears(now, 100),
  };
};

export const normalizeDateWithinBounds = (value, minDate, maxDate) => {
  if (!value) {
    return { date: null, hasError: false };
  }

  const toComparable = (date) => {
    if (!date || !isValid(date)) {
      return null;
    }
    return date.getUTCFullYear() * 10000 + (date.getUTCMonth() + 1) * 100 + date.getUTCDate();
  };

  const isWithinBounds = (candidate) => {
    if (!isValid(candidate)) {
      return false;
    }

    const candidateValue = toComparable(candidate);

    if (minDate && isValid(minDate)) {
      if (candidateValue < toComparable(minDate)) {
        return false;
      }
    }

    if (maxDate && isValid(maxDate)) {
      if (candidateValue > toComparable(maxDate)) {
        return false;
      }
    }

    return true;
  };

  const tryNormalize = (candidate) => {
    if (!candidate) {
      return null;
    }
    if (isWithinBounds(candidate)) {
      return { date: format(candidate, "yyyy-MM-dd"), hasError: false };
    }
    return null;
  };

  const directDate = value instanceof Date ? value : parse(String(value), "MM/dd/yyyy", new Date());
  const normalized = tryNormalize(directDate) ?? tryNormalize(new Date(value));

  if (normalized) {
    return normalized;
  }

  return { date: null, hasError: true };
};

export const isWithinDateBounds = (date, minDate, maxDate) => {
  if (!date || !isValid(date)) {
    return false;
  }

  const toComparable = (candidate) =>
    candidate.getUTCFullYear() * 10000 +
    (candidate.getUTCMonth() + 1) * 100 +
    candidate.getUTCDate();

  const candidateValue = toComparable(date);

  if (minDate && isValid(minDate)) {
    const minValue = toComparable(minDate);
    if (candidateValue < minValue) {
      return false;
    }
  }

  if (maxDate && isValid(maxDate)) {
    const maxValue = toComparable(maxDate);
    if (candidateValue > maxValue) {
      return false;
    }
  }

  return true;
};

export const formatDateAppointment = (date) => {
  return format(date || new Date(), "yyyy-MM-dd");
};

/**
 * Validates if all required fields in a form have valid values.
 *
 * @param {Object} obj - The validation parameters
 * @param {Object} obj.formData - The form data object containing field values
 * @param {Object} obj.requiredFields - Object mapping field names to boolean indicating if required
 * @returns {boolean} - Returns true if all required fields have values, false otherwise
 */
export function hasAllRequiredFields({ formData, requiredFields }) {
  for (const [key, value] of Object.entries(formData)) {
    const isRequired = requiredFields[key];
    if (isRequired && (value === null || value === undefined || value === "")) {
      return false;
    }
  }

  return true;
}

/**
 * Validates if something has changed in the form.
 *
 * @param {Object} initialData - The form data object containing initial values
 * @param {Object} formData - The form data object containing current values
 * @returns {boolean} - Returns true if something changed, false otherwise
 */
export function hasChanges(initialData, formData) {
  for (const key in formData) {
    if (Object.prototype.hasOwnProperty.call(initialData, key)) {
      const initialDataStringified = JSON.stringify(initialData[key]);
      const formDataStringified = JSON.stringify(formData[key]);
      if (initialDataStringified !== formDataStringified) {
        return true;
      }
    } else {
      return true;
    }
  }
  return false;
}

export default null;
